/**
 * Settings, Approvals: "Who answers what rules leave open" (ORGU-81). Orgus or People, one
 * choice for the hub; owners and admins change it, everyone else reads it.
 */
import { useState } from 'react';
import { Bot } from '@orgus/ui/icons';
import { APPROVAL_ANSWERER_LABEL } from '@orgus/protocol';
import type { ApprovalAnswerer } from '@orgus/protocol';
import { Panel, Segmented, toUiError, toast } from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { useApprovals } from './live.ts';
import { ANSWERER_CAPTION, answererOf, saveAnswerer } from './answererModel.ts';

export function AnswererPanel() {
  const { approvals } = useApprovals();
  const canEdit = useCan('admin');
  const [busy, setBusy] = useState(false);
  const current = answererOf(approvals);
  if (!current) return null;

  const choose = async (next: ApprovalAnswerer) => {
    if (next === current || busy) return;
    setBusy(true);
    try {
      await saveAnswerer(next);
      toast(next === 'orgus' ? 'Orgus answers what rules leave open' : 'People answer what rules leave open', { tone: 'success' });
    } catch (e) {
      const u = toUiError(e);
      toast(`Could not change who answers: ${u.what}`, { tone: 'error', description: u.next });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Who answers what rules leave open"
      icon={<Bot />}
      actions={
        <Segmented<ApprovalAnswerer>
          label="Who answers what rules leave open"
          value={current}
          onChange={(v) => void choose(v)}
          options={(['orgus', 'people'] as const).map((v) => ({ value: v, label: APPROVAL_ANSWERER_LABEL[v], disabled: !canEdit || busy }))}
        />
      }
    >
      <p className="px-4 pb-4 text-caption text-fg-subtle">{ANSWERER_CAPTION[current]}</p>
    </Panel>
  );
}
